import { create } from 'zustand';
import api from '@services/api';
import { StorageService } from '@utils/storage';
import { Subscriber, SubscriberState } from '@appTypes/subscriber.types';

/**
 * Subscriber store for managing subscriber selection
 * Matches Angular's subscriber.service.ts and auth-session.service.ts patterns
 */
export const useSubscriberStore = create<SubscriberState>((set, get) => ({
    currentSubscriber: null,
    subscribers: [],
    subscriberId: StorageService.getSubscriberId(),
    isLoading: false,
    error: null,

    /**
     * Set the current subscriber ID
     * Optionally reload the page (matches Angular behavior)
     */
    setSubscriberId: (id: number | null, reload = false) => {
        if (id) {
            StorageService.updateSubscriberId(id);
        } else {
            StorageService.clearSubscriberId();
        }
        set({ subscriberId: id });

        if (reload) {
            window.location.reload();
        }
    },

    /**
     * Load all subscribers (admin only)
     */
    loadSubscribers: async () => {
        try {
            set({ isLoading: true, error: null });
            const response = await api.get<Subscriber[]>('/subscribers');
            set({
                subscribers: response.data,
                isLoading: false,
            });
        } catch (error) {
            console.error('Failed to load subscribers:', error);
            set({
                isLoading: false,
                error: 'Failed to load subscribers',
            });
        }
    },

    /**
     * Load the current subscriber details
     */
    loadCurrentSubscriber: async () => {
        const subscriberId = get().subscriberId || StorageService.getSubscriberId();
        if (!subscriberId) {
            return;
        }

        try {
            set({ isLoading: true, error: null });
            const response = await api.get<Subscriber>(`/subscribers/${subscriberId}`);
            set({
                currentSubscriber: response.data,
                subscriberId,
                isLoading: false,
            });
        } catch (error) {
            console.error('Failed to load current subscriber:', error);
            set({
                isLoading: false,
                error: 'Failed to load subscriber',
            });
        }
    },

    /**
     * Get subscriber ID from store or storage
     */
    getSubscriberId: () => {
        return get().subscriberId || StorageService.getSubscriberId();
    },

    /**
     * Update subscriber ID (matches Angular's authServerProvider.updateSubscriberId)
     */
    updateSubscriberId: (id: number) => {
        StorageService.updateSubscriberId(id);
        set({ subscriberId: id });
    },

    /**
     * Clear all subscriber data
     */
    clearSubscribers: () => {
        set({
            currentSubscriber: null,
            subscribers: [],
            subscriberId: null,
        });
    },
}));

export default useSubscriberStore;
